import { api, type CursorPage, type Page } from "@/lib/api/client";
import type {
  Availability,
  AvailabilityFilters,
  BalanceFilters,
  BarcodeCreateInput,
  BarcodeFilters,
  BarcodeUpdateInput,
  CategoryCreateInput,
  CategoryFilters,
  CategoryUpdateInput,
  CountLinesInput,
  CountOpenInput,
  CountPostInput,
  DocumentCheck,
  DocumentCreateInput,
  DocumentFilters,
  DocumentHeaderUpdateInput,
  DocumentLinesInput,
  DocumentPostInput,
  InventoryDocument,
  CountFilters,
  InventoryListFilters,
  InventoryLot,
  Gs1Lookup,
  Gs1Mode,
  ItsFilters,
  ItsNotification,
  ItsNotificationDetail,
  LedgerEntry,
  LedgerFilters,
  LocationCreateInput,
  LocationFilters,
  LocationUpdateInput,
  LotCreateInput,
  LotFilters,
  LotUpdateInput,
  Product,
  ProductBarcode,
  ProductCategory,
  ProductCreateInput,
  ProductFilters,
  ProductUpdateInput,
  ReorderRule,
  ReorderRuleCreateInput,
  ReorderRuleFilters,
  ReorderRuleUpdateInput,
  Reservation,
  ReservationFilters,
  Serial,
  SerialCreateInput,
  SerialFilters,
  SerialHistory,
  Unit,
  UnitCreateInput,
  UnitFilters,
  UnitUpdateInput,
  UomConversion,
  UomConversionCreateInput,
  UomConversionFilters,
  UomConversionUpdateInput,
  WarehouseLocation,
  StockBalance,
  StockCount,
  Warehouse,
  WarehouseCreateInput,
  WarehouseFilters,
  WarehouseUpdateInput,
} from "./inventory.types";

/**
 * The Stok module's HTTP surface.
 *
 * Same dialect as the Kuruluş resources and the same reason for a factory: every one of these
 * lists on `page/limit/q/sortBy/sortOrder`, creates with a body, patches with a body carrying
 * `version`, soft-deletes and restores. Written out per resource this file would be a place
 * where one path is a typo and nobody notices until a screen 404s.
 *
 * The module has eighteen resources; this file grows one entry at a time as the screens land,
 * rather than declaring all eighteen against screens that do not exist yet.
 */
/** What may travel in a query string — the client's own parameter contract. */
type QueryValue = string | number | boolean | undefined;

function resource<
  TRow,
  TCreate,
  TUpdate,
  TFilters extends Record<string, QueryValue> = InventoryListFilters,
>(base: string) {
  return {
    list(filters: TFilters): Promise<Page<TRow>> {
      return api.page<TRow>(base, { params: { ...filters } });
    },
    get(id: string): Promise<TRow> {
      return api.get<TRow>(`${base}/${id}`);
    },
    create(body: TCreate): Promise<TRow> {
      return api.post<TRow>(base, body);
    },
    /** `version` travels with every patch — the server answers 409 `STALE_VERSION` on a stale one. */
    update(id: string, body: TUpdate): Promise<TRow> {
      return api.patch<TRow>(`${base}/${id}`, body);
    },
    /** Soft delete: the row keeps its ledger history and can be restored. */
    remove(id: string): Promise<void> {
      return api.delete(`${base}/${id}`);
    },
    restore(id: string): Promise<TRow> {
      return api.post<TRow>(`${base}/${id}/restore`);
    },
  };
}

export const inventoryApi = {
  products: resource<Product, ProductCreateInput, ProductUpdateInput, ProductFilters>(
    "inventory/products",
  ),
  warehouses: resource<Warehouse, WarehouseCreateInput, WarehouseUpdateInput, WarehouseFilters>(
    "inventory/warehouses",
  ),
  lots: resource<InventoryLot, LotCreateInput, LotUpdateInput, LotFilters>("inventory/lots"),

  /**
   * Counts. Each transition is its own endpoint because each does something different to the
   * ledger; the empty body is what the server's transition schema expects.
   */
  counts: {
    ...resource<StockCount, never, never, CountFilters>("inventory/counts"),
    /**
     * «Yeni sayım» (17 Ekim 2026). `@Idempotent` on the server: the key is bound to the
     * submission, so a retried timeout opens ONE count, not two snapshots of the same shelves.
     * Only `id` is read from the answer — a replay carries nothing else (`replay: "reference"`).
     */
    open: (body: CountOpenInput, idempotencyKey: string) =>
      api.post<StockCount>("inventory/counts", body, { idempotencyKey }),
    /**
     * Counted quantities, up to 500 lines a call. An UPSERT per (product, shelf, lot): sending the
     * same figure twice leaves the same line, so a retry needs no key. A line the snapshot did not
     * hold is stock found on the shelf, added with a system figure of zero.
     */
    enterLines: (id: string, body: CountLinesInput) =>
      api.post<StockCount>(`inventory/counts/${id}/lines`, body),
    start: (id: string) => api.post<StockCount>(`inventory/counts/${id}/start`, {}),
    submit: (id: string) => api.post<StockCount>(`inventory/counts/${id}/submit`, {}),
    /**
     * The reason travels in the body (`countTransitionSchema` takes an optional `reason`), and the
     * audit interceptor writes a body's `reason` onto the trail row. Until 9 Ekim 2026 the screen
     * asked for one, said «Denetim kaydına yazılır», and sent `{}`.
     */
    cancel: (id: string, body: { reason?: string } = {}) =>
      api.post<StockCount>(`inventory/counts/${id}/cancel`, body),
    /**
     * The difference becomes an ADJUSTMENT document; `reason` is written on it and in the audit.
     * REQUIRED on the server since 17 Ekim 2026 (it was the screen's rule alone until then).
     */
    post: (id: string, body: CountPostInput) =>
      api.post<StockCount>(`inventory/counts/${id}/post`, body),
  },

  /**
   * Stock documents. The two state transitions are separate calls, not a status patch: posting
   * writes the ledger and cancelling does not, and an endpoint that took `status` as a field
   * would let a client ask for the first while believing it asked for the second.
   */
  documents: {
    ...resource<InventoryDocument, never, never, DocumentFilters>("inventory/documents"),
    /**
     * Opens a DRAFT — header and lines together (17 Ekim 2026). Nothing moves until posting.
     * `@Idempotent` with `replay: "reference"`: the caller reads `id` only and re-reads the row.
     */
    create(body: DocumentCreateInput, idempotencyKey: string): Promise<InventoryDocument> {
      return api.post<InventoryDocument>("inventory/documents", body, { idempotencyKey });
    },
    /** Header fields of a draft; `version` from the row the screen read. */
    updateHeader(id: string, body: DocumentHeaderUpdateInput): Promise<InventoryDocument> {
      return api.patch<InventoryDocument>(`inventory/documents/${id}`, body);
    },
    /** The WHOLE line set of a draft, validated and swapped as one. */
    replaceLines(id: string, body: DocumentLinesInput): Promise<InventoryDocument> {
      return api.patch<InventoryDocument>(`inventory/documents/${id}/lines`, body);
    },
    /** «Kontrol»: what posting would do and why it would be refused. Writes nothing. */
    check(id: string): Promise<DocumentCheck> {
      return api.get<DocumentCheck>(`inventory/documents/${id}/check`);
    },
    /**
     * Writes the ledger. `reason` REQUIRED (17 Ekim 2026) and `idempotencyKey` sent: posting is
     * already safe twice on the server (a conditional claim), the key makes the intent explicit.
     */
    post(id: string, body: DocumentPostInput): Promise<InventoryDocument> {
      return api.post<InventoryDocument>(`inventory/documents/${id}/post`, body);
    },
    cancel(id: string, body: { version: number; reason: string }): Promise<InventoryDocument> {
      return api.post<InventoryDocument>(`inventory/documents/${id}/cancel`, body);
    },
  },

  /**
   * Balances are the ledger's sum, never a record. Read only — there is no create, update or
   * delete, because the way to change a balance is to post a document.
   */
  balances: {
    list(filters: BalanceFilters): Promise<Page<StockBalance>> {
      return api.page<StockBalance>("inventory/balances", { params: { ...filters } });
    },
  },

  /**
   * The stock ledger — CURSOR-paged, and `api.cursor` rather than `api.page`.
   *
   * The choice is the server's and it is correct: the ledger is append-only and grows while
   * somebody reads it, so an OFFSET into it shows rows twice or skips them, and "sayfa 40 / 812"
   * is wrong by the time it renders. A cursor names a position instead of a distance.
   *
   * Reading it through `api.page` would not fail — the envelope carries `meta.cursor` and no
   * `meta.pagination`, so the helper would synthesise a single page and the screen would show
   * the first hundred movements and claim that was all of them.
   */
  ledger: {
    list(filters: LedgerFilters): Promise<CursorPage<LedgerEntry>> {
      return api.cursor<LedgerEntry>("inventory/ledger", { params: { ...filters } });
    },
  },

  /**
   * Reservations hold stock that is still physically present.
   *
   * Releasing is its own endpoint, not a status patch: a release returns quantity to
   * availability and is audited, while `status` is an outcome the server decides. An endpoint
   * that accepted the field would let a client mark something CONSUMED without consuming it.
   */
  reservations: {
    list(filters: ReservationFilters): Promise<Page<Reservation>> {
      return api.page<Reservation>("inventory/reservations", { params: { ...filters } });
    },
    release(id: string, body: { reason?: string } = {}): Promise<Reservation> {
      return api.post<Reservation>(`inventory/reservations/${id}/release`, body);
    },
  },

  reorderRules: resource<
    ReorderRule,
    ReorderRuleCreateInput,
    ReorderRuleUpdateInput,
    ReorderRuleFilters
  >("inventory/reorder-rules"),

  /**
   * The İTS declaration queue.
   *
   * `retry` and `cancel` are guarded by their own permission because they speak to an OUTSIDE
   * AUTHORITY: re-declaring to TİTCK is not a warehouse action that follows from being allowed
   * to post a document, and a cancelled declaration stays on the row with its reason, because a
   * regulator queue is evidence and evidence is not deleted.
   */
  its: {
    list(filters: ItsFilters): Promise<Page<ItsNotification>> {
      return api.page<ItsNotification>("inventory/its-notifications", { params: { ...filters } });
    },
    get(id: string): Promise<ItsNotificationDetail> {
      return api.get<ItsNotificationDetail>(`inventory/its-notifications/${id}`);
    },
    /** `note`, not `reason`: the server's body is a strict `{ note? }` and refuses other keys. */
    retry(id: string, body: { note?: string } = {}): Promise<ItsNotification> {
      return api.post<ItsNotification>(`inventory/its-notifications/${id}/retry`, body);
    },
    cancel(id: string, body: { reason: string }): Promise<ItsNotification> {
      return api.post<ItsNotification>(`inventory/its-notifications/${id}/cancel`, body);
    },
  },

  /**
   * Karekod sorgulama — READ ONLY, and the endpoint is a GET for that reason.
   *
   * HARD RULE from the screen contract: scanning a box to see where it is must never be the
   * thing that moves it. Every action this screen offers afterwards is a separate, named write
   * behind its own permission.
   */
  lookup: {
    gs1(params: { code: string; mode?: Gs1Mode; productId?: string }): Promise<Gs1Lookup> {
      return api.get<Gs1Lookup>("inventory/lookup/gs1", { params: { ...params } });
    },
    /** Every movement of ONE box, oldest first. Not paged: a serial has a handful of events. */
    serialHistory(id: string, params: { from?: string; to?: string } = {}): Promise<SerialHistory> {
      return api.get<SerialHistory>(`inventory/serials/${id}/history`, { params: { ...params } });
    },
  },

  /**
   * The definitions everything else is measured in.
   *
   * `units` stopped being read-only reference data the moment there was a screen to manage it:
   * the repository's own rule is that a quantity always renders with its unit, and a unit
   * nobody can define makes that rule unkeepable.
   */
  units: resource<Unit, UnitCreateInput, UnitUpdateInput, UnitFilters>("inventory/units"),

  /**
   * PER PRODUCT, not global — and that is the whole design. "One case is twelve items" is true
   * of a particular article and false of the next one; a global table would make the first
   * product that disagrees impossible to express.
   */
  uomConversions: resource<
    UomConversion,
    UomConversionCreateInput,
    UomConversionUpdateInput,
    UomConversionFilters
  >("inventory/uom-conversions"),

  locations: resource<WarehouseLocation, LocationCreateInput, LocationUpdateInput, LocationFilters>(
    "inventory/locations",
  ),

  categories: resource<ProductCategory, CategoryCreateInput, CategoryUpdateInput, CategoryFilters>(
    "inventory/product-categories",
  ),

  barcodes: resource<ProductBarcode, BarcodeCreateInput, BarcodeUpdateInput, BarcodeFilters>(
    "inventory/product-barcodes",
  ),

  /**
   * Serials are CREATED by receiving goods, not by typing them — so no update here. The list
   * exists to find one box among millions; correcting it is a document, not an edit.
   */
  serials: {
    ...resource<Serial, SerialCreateInput, never, SerialFilters>("inventory/serials"),
  },

  /**
   * Kullanılabilirlik: on-hand MINUS what is already reserved.
   *
   * A separate endpoint from `balances` because it answers a different question. The balance
   * says what is in the building; this says what may still be promised to somebody. A sales
   * order that read the balance would sell stock another order is holding — which is why this
   * is the one the order screen calls.
   */
  availability: {
    get(filters: AvailabilityFilters): Promise<Availability> {
      return api.get<Availability>("inventory/availability", { params: { ...filters } });
    },
  },
};

/** Narrowed list helpers, so a caller cannot pass a filter the endpoint does not accept. */
export const inventoryLists = {
  products: (filters: ProductFilters) => inventoryApi.products.list(filters),
  warehouses: (filters: WarehouseFilters) => inventoryApi.warehouses.list(filters),
};
